
import { useState, useEffect, useMemo } from "react";
import { Tables } from "@/integrations/supabase/types";
import { ActivitiesHeader } from "./activities/ActivitiesHeader";
import { ActivitiesGrid } from "./activities/ActivitiesGrid";
import { ActivityEditDialog } from "./activities/ActivityEditDialog";
import { useActivityOperations } from "./activities/hooks/useActivityOperations";
import { useActivityCategories } from "./activities/hooks/useActivityCategories";
import { AdminSearch } from "./shared/AdminSearch";
import { AdminFilters } from "./shared/AdminFilters";
import { AdminPagination } from "./shared/AdminPagination";
import { usePagination } from "@/hooks/usePagination";

type Activity = Tables<"activities">;

export const ActivitiesManager = () => {
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    category: "",
    difficulty: "",
    type: "",
  });
  
  const {
    activities,
    loading: activitiesLoading,
    fetchActivities,
    saveActivity,
    deleteActivity,
  } = useActivityOperations();

  const {
    categories,
    loading: categoriesLoading,
  } = useActivityCategories();

  useEffect(() => {
    fetchActivities();
  }, []);

  // Filter and search activities
  const filteredActivities = useMemo(() => {
    return activities.filter((activity) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          activity.title.toLowerCase().includes(searchLower) ||
          activity.description?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters.status && activity.status !== filters.status) {
        return false;
      }

      // Category filter
      if (filters.category && activity.category_id !== filters.category) {
        return false;
      }

      // Difficulty filter
      if (filters.difficulty && activity.difficulty !== filters.difficulty) {
        return false;
      }

      // Type filter
      if (filters.type && activity.type !== filters.type) {
        return false;
      }

      return true;
    });
  }, [activities, searchTerm, filters]);

  // Pagination
  const pagination = usePagination({
    items: filteredActivities,
    itemsPerPage: 10,
  });

  const handleSave = async (formData: any) => {
    const success = await saveActivity(formData, editingActivity?.id);
    if (success) {
      setEditingActivity(null);
    }
    return success;
  };

  const handleEdit = (activity: Activity) => {
    setEditingActivity(activity);
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingActivity(null);
    setDialogOpen(true);
  };

  const handleDelete = async (activityId: string) => {
    await deleteActivity(activityId);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    pagination.resetPagination();
  };

  const handleResetFilters = () => {
    setFilters({ status: "", category: "", difficulty: "", type: "" });
    setSearchTerm("");
    pagination.resetPagination();
  };

  if (activitiesLoading || categoriesLoading) {
    return <div className="text-center">Loading activities...</div>;
  }

  const categoryOptions = categories.map(cat => ({
    value: cat.id,
    label: cat.name,
  }));

  const typeOptions = [
    { value: "worksheet", label: "Worksheet" },
    { value: "game", label: "Game" },
    { value: "craft", label: "Craft" },
    { value: "video", label: "Video" },
    { value: "interactive", label: "Interactive" },
  ];

  const hasFilters = Boolean(searchTerm || Object.values(filters).some(v => v));

  return (
    <div className="space-y-4 md:space-y-6 max-w-full overflow-hidden">
      <ActivitiesHeader onAddActivity={handleCreate} />

      {/* Search and Filters */}
      <div className="space-y-4">
        <AdminSearch
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search activities..."
          className="max-w-md"
        />
        
        <AdminFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          categoryOptions={categoryOptions}
          typeOptions={typeOptions}
        />
      </div>

      <ActivityEditDialog
        activity={editingActivity}
        categories={categories}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={handleSave}
      />

      <ActivitiesGrid
        activities={pagination.paginatedItems}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {filteredActivities.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          {hasFilters 
            ? "No activities match your search criteria." 
            : "No activities found. Click 'Add Activity' to create your first activity."
          }
        </div>
      )}

      <AdminPagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        itemsPerPage={10}
        totalItems={filteredActivities.length}
        onPageChange={pagination.goToPage}
        onItemsPerPageChange={() => {}} // Fixed items per page for now
      />
    </div>
  );
};
